// One table maps every shell page id to a URL and a sidebar group.
//
// The rest of the app still navigates by page id (`navigateTo('logic')`); the
// navigation store turns that id into a path with `pathForPage` and back with
// `pageForPath`, so every screen gets a URL, the back button works and a
// refresh lands where it was — without touching the hundreds of call sites.

export type NavGroupId =
  | 'workspace' | 'connect' | 'model' | 'build' | 'operate' | 'analyze' | 'admin' | 'maic' | 'help';

export interface RouteDef {
  /** Page id used by navigateTo and renderPage. */
  page: string;
  /** URL path, no trailing slash. */
  path: string;
  group: NavGroupId;
  /** Module the permission system gates this page by, when it differs from `page`. */
  permissionModule?: string;
  /** Sidebar item that stays highlighted on this page, when it has none of its own. */
  navParent?: string;
}

export const ROUTES: RouteDef[] = [
  { page: 'home',           path: '/home',                      group: 'workspace' },
  { page: 'files',          path: '/files',                     group: 'workspace' },
  { page: 'human-actions',  path: '/inbox',                     group: 'workspace' },

  { page: 'connectors',     path: '/connect/connectors',        group: 'connect' },
  { page: 'data',           path: '/connect/data',              group: 'connect' },
  // Two tabs of Data with their own link (they entered dev after the redesign
  // branch: Analysis #1330, Lineage #1305). The Data page opens on that tab.
  { page: 'data-analysis',  path: '/connect/data/analysis',     group: 'connect', permissionModule: 'data', navParent: 'data' },
  // A dataset is a resource with its own address: /connect/data/datasets/<id> (C16).
  { page: 'data-datasets',  path: '/connect/data/datasets',     group: 'connect', permissionModule: 'data', navParent: 'data' },
  { page: 'data-lineage',   path: '/connect/data/lineage',      group: 'connect', permissionModule: 'data', navParent: 'data' },
  // Old addresses of the Explorer and the Records grid (C14): with ui_v2 they
  // are one Explorer; the grid is its "Tabla editable" view.
  { page: 'data-explorer',  path: '/connect/data/explorer',     group: 'connect', permissionModule: 'data', navParent: 'data' },
  { page: 'data-grid',      path: '/connect/data/grid',         group: 'connect', permissionModule: 'data', navParent: 'data' },
  { page: 'pipelines',      path: '/connect/pipelines',         group: 'connect' },
  { page: 'utilities',      path: '/connect/utilities',         group: 'connect' },

  { page: 'ontology',       path: '/model/ontology',            group: 'model' },
  // One record, full page: /o/<object type id>/<record id>. Page id is
  // `object:<type>/<record>`; bare 'object' has nothing to show.
  // Action types live in the ontology (C11, 3.25): the index of all of them.
  // The catalog tab of the Inbox is an alias of this page with ui_v2.
  { page: 'action-types',   path: '/model/actions',             group: 'model', permissionModule: 'human-actions', navParent: 'ontology' },
  { page: 'object',         path: '/o',                         group: 'model', permissionModule: 'ontology', navParent: 'ontology' },

  { page: 'logic',          path: '/build/logic',               group: 'build' },
  { page: 'agents',         path: '/build/agents',              group: 'build' },
  { page: 'external-apps',  path: '/build/app-studio',          group: 'build' },
  { page: 'workbench',      path: '/build/workbench',           group: 'build' },
  { page: 'evals',          path: '/build/evals',               group: 'build' },

  { page: 'apps',           path: '/operate/dashboards',        group: 'operate' },
  { page: 'apps-app',       path: '/operate/apps',              group: 'operate' },
  { page: 'operations',     path: '/operate/operations',        group: 'operate' },

  { page: 'process-mining', path: '/analyze/process-mining',    group: 'analyze', permissionModule: 'activity' },
  { page: 'insights',       path: '/analyze/insights',          group: 'analyze' },
  { page: 'insights-runs',  path: '/analyze/insights/runs',     group: 'analyze', permissionModule: 'insights' },
  { page: 'predictions',    path: '/analyze/predictions',       group: 'analyze' },
  { page: 'value',          path: '/analyze/value',             group: 'analyze' },
  { page: 'scenarios',      path: '/analyze/scenarios',         group: 'analyze' },
  // Documents with live blocks (QUIVER-11, #1381): Foundry keeps Notepad under
  // analysis. Gated like Dashboards, which it is built on.
  { page: 'documents',      path: '/analyze/documents',         group: 'analyze', permissionModule: 'apps' },
  { page: 'discovery',      path: '/analyze/discovery',         group: 'analyze' },
  // Solution designs (SCOPING-1): app rows read like dashboards, next to
  // Discovery, where a use case is scoped before it is built.
  { page: 'solution-design', path: '/analyze/solution-design', group: 'analyze', permissionModule: 'apps' },
  { page: 'task-mining',    path: '/analyze/task-mining',       group: 'analyze' },
  { page: 'mirror',         path: '/analyze/mirror',            group: 'analyze' },

  // Branches and change requests sit in Administración (no group of their
  // own in this step); the URLs keep the /release prefix.
  { page: 'branches',       path: '/release/branches',          group: 'admin' },
  { page: 'changes',        path: '/release/changes',           group: 'admin' },
  { page: 'solutions',      path: '/release/solutions',         group: 'admin' },

  { page: 'activity',       path: '/admin/activity',            group: 'admin' },
  { page: 'admin',          path: '/admin',                     group: 'admin' },
  { page: 'settings',       path: '/admin/settings',            group: 'admin' },
  // Cuenta (C21): what is personal to the signed-in user, reached from the
  // user menu of the new interface.
  { page: 'account',        path: '/account',                   group: 'admin' },
  { page: 'platform',       path: '/platform',                  group: 'admin' },
  { page: 'incidents',      path: '/platform/incidents',        group: 'admin' },

  { page: 'projects',       path: '/maic/projects',             group: 'maic' },
  { page: 'finance',        path: '/maic/finance',              group: 'maic' },

  // Academy owns the paths under /academy (tracks, lessons, exam, programs:
  // PR #1375, GSL-2); the shell only maps the whole subtree to the page.
  // /academy/catalog is public and renders outside the shell.
  { page: 'academy',        path: '/academy',                   group: 'help' },
];

/** Old or mistaken page ids still called somewhere, mapped to the page that
 *  shows them. Before this map, each of these fell through renderPage's
 *  default and opened Connectors. */
export const PAGE_ALIASES: Record<string, string> = {
  graph: 'ontology',
  process: 'process-mining',
  events: 'activity',
  audit: 'activity',
  gateway: 'settings',
};

const BY_PAGE = new Map(ROUTES.map(r => [r.page, r]));
// Longest path first so '/analyze/insights/runs' wins over '/analyze/insights'
// and '/admin/settings' over '/admin'.
const BY_PATH_DESC = [...ROUTES].sort((a, b) => b.path.length - a.path.length);

/** Landing page when the URL names none. Dashboards, as before the redesign;
 *  with the new interface (ui_v2) the shell sends you to Home instead. */
export const DEFAULT_PAGE = 'apps';
export const DEFAULT_PAGE_V2 = 'home';

export function resolvePage(page: string): string {
  return PAGE_ALIASES[page] ?? page;
}

export function objectPage(objectTypeId: string, recordId: string): string {
  return `object:${objectTypeId}/${recordId}`;
}

/** The (type, record) an `object:` page id points at, or null. */
export function parseObjectPage(page: string): { objectTypeId: string; recordId: string } | null {
  if (!page.startsWith('object:')) return null;
  const rest = page.slice('object:'.length);
  const slash = rest.indexOf('/');
  if (slash <= 0 || slash === rest.length - 1) return null;
  return { objectTypeId: rest.slice(0, slash), recordId: rest.slice(slash + 1) };
}

export function routeForPage(page: string): RouteDef | undefined {
  if (page.startsWith('object:')) return BY_PAGE.get('object');
  if (page.startsWith('app-')) return BY_PAGE.get('apps');
  if (page.startsWith('external-app:')) return BY_PAGE.get('external-apps');
  return BY_PAGE.get(resolvePage(page));
}

/** Sidebar item to highlight for a page (its own id, or its parent's). */
export function navItemFor(page: string): string {
  const route = routeForPage(page);
  return route?.navParent ?? route?.page ?? page;
}

/** Module id the permission system judges a page by. */
export function permissionModuleFor(page: string): string {
  const route = routeForPage(page);
  return route?.permissionModule ?? route?.page ?? page;
}

/** Pages that open one resource by id in their URL, /…/<page>/<id>[/<view>],
 *  plus ?tab= for its tab (step B1; shell/resourceRoutes.ts reads and writes
 *  the id and the tab). The page itself stays the same page id. */
export const RESOURCE_PAGES = new Set([
  'connectors', 'pipelines', 'ontology', 'logic', 'agents', 'evals', 'data-analysis', 'operations',
  // C23: model, use case, scenario, project and process of the analysis area.
  'predictions', 'value', 'scenarios', 'discovery', 'task-mining', 'mirror',
]);

/** Pages that open one app row by id in their URL (/…/<page>/<id>). */
// Datasets of Datos (C16) open by id like the pages above.
RESOURCE_PAGES.add('data-datasets');

// Processes of Minería de procesos (C19): /analyze/process-mining/<id>?tab=<view>.
RESOURCE_PAGES.add('process-mining');

const APP_PAGES = new Set(['apps', 'apps-app', 'documents', 'solution-design']);

/** URL for a page id plus the app opened inside it, if any. */
export function pathForPage(page: string, appId?: string | null): string {
  const obj = parseObjectPage(page);
  if (obj) return `/o/${encodeURIComponent(obj.objectTypeId)}/${encodeURIComponent(obj.recordId)}`;
  if (page.startsWith('external-app:')) {
    return `/build/app-studio/${encodeURIComponent(page.slice('external-app:'.length))}`;
  }
  if (page.startsWith('app-')) return `/operate/dashboards/${encodeURIComponent(page.slice(4))}`;
  const route = BY_PAGE.get(resolvePage(page));
  if (!route) return '/';
  if (appId && APP_PAGES.has(route.page)) {
    return `${route.path}/${encodeURIComponent(appId)}`;
  }
  return route.path;
}

export interface ParsedLocation {
  page: string;
  appId: string | null;
}

/** Inverse of pathForPage. Null when the path is not a shell page ('/' or
 *  an unknown URL), so the caller keeps its default. */
export function pageForPath(pathname: string): ParsedLocation | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return null;
  for (const route of BY_PATH_DESC) {
    if (path === route.path) return { page: route.page, appId: null };
    if (!path.startsWith(route.path + '/')) continue;
    if (route.page === 'object') {
      const parts = path.slice(route.path.length + 1).split('/');
      if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
      return { page: objectPage(decodeURIComponent(parts[0]), decodeURIComponent(parts[1])), appId: null };
    }
    if (route.page === 'academy') {
      return path.startsWith(`${route.path}/catalog`) ? null : { page: 'academy', appId: null };
    }
    const rest = decodeURIComponent(path.slice(route.path.length + 1));
    if (route.page === 'external-apps') return { page: `external-app:${rest}`, appId: null };
    if (APP_PAGES.has(route.page)) return { page: route.page, appId: rest };
    if (RESOURCE_PAGES.has(route.page)) return { page: route.page, appId: null };
  }
  return null;
}
